import React, { FC, useRef } from 'react';
import { Form as FormikForm, Formik, FormikHelpers, FormikProps, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { flattenDeep, pick, values } from 'lodash';

import {
  LeaderboardConfig,
  useCreateUpdateLeaderboardConfigMutation,
  useModifyLeaderboardScoresMutation,
} from 'api';
import * as paths from 'paths';
import { useNotify } from 'hooks';

import { LeaderboardSchema } from 'features/admin/validations';
import { LeaderboardForm } from 'features/admin/components/Leaderboards/Partials';
import { useUpdateLeaderboard } from 'features/admin/updaters';

interface Props {
  config: LeaderboardConfig;
}

const LeaderboardsEdit: FC<Props> = ({ config }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const navigate = useNavigate();

  const mutationOptions = useUpdateLeaderboard();
  const { mutateAsync: createAsyncLeaderboardConfig } = useCreateUpdateLeaderboardConfigMutation({
    onError: notify.mutationError,
    ...mutationOptions,
  });

  const { mutate: updateLeaderboardScores } = useModifyLeaderboardScoresMutation({
    onError: notify.mutationError,
    onSuccess,
  });

  const initialFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: getInitialValues(),
  });

  const formikRef = useRef<FormikProps<LeaderboardConfig>>(initialFormikRef);

  function getInitialValues(): LeaderboardConfig {
    return pick(
      config,
      'id',
      'title',
      'description',
      'leaderboard_status',
      'created_at',
      'start_at',
      'end_at',
      'all_users',
      'segment_ids',
      'scoring_aggregate'
    );
  }

  function getScores() {
    const events = values(formikRef.current.values.scoring_aggregate);
    const scores = events.map(event => values(event));
    return flattenDeep(scores);
  }

  function onSuccess() {
    notify.info({ message: t('components:savedSuccessfully') });
    navigate(paths.adminLeaderboards);
  }

  async function handleSubmit(
    values: LeaderboardConfig,
    formikHelpers: FormikHelpers<LeaderboardConfig>
  ) {
    try {
      const scores = getScores();

      const submitValues = { ...values };

      delete submitValues.created_at;
      delete submitValues.scoring_aggregate;

      await createAsyncLeaderboardConfig({ data: submitValues }).then(data => {
        updateLeaderboardScores({
          id: data.createUpdateLeaderboardConfig.id,
          scores,
        });
      });
    } catch (e) {
      formikHelpers.setSubmitting(false);
      notify.mutationError();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={formikRef.current.initialValues}
      innerRef={ref => (formikRef.current = ref)}
      onSubmit={handleSubmit}
      validationSchema={LeaderboardSchema(true)}
    >
      <FormikForm>
        <LeaderboardForm />
      </FormikForm>
    </Formik>
  );
};

export default LeaderboardsEdit;
